import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface AgeGroup {
  id: number;
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min: string;
  birth_date_max: string;
  created_at?: string;
}

interface Coach {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface Assignment {
  id: number;
  age_group_id: number;
  coach_id: string;
}

interface NewAgeGroup {
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min: string;
  birth_date_max: string;
}

export function useAgeGroupsManagement() {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [editingGroup, setEditingGroup] = useState<AgeGroup | null>(null);

  const fetchAgeGroups = useCallback(async () => {
    setLoading(true);
    const { data: groups, error: groupsError } = await supabase
      .from('age_groups')
      .select('*')
      .order('group_name', { ascending: true });
    
    if (groupsError) {
      setError(groupsError.message);
      setLoading(false);
      return;
    }
    
    setAgeGroups(groups || []);
    setError(null);
    setLoading(false);
  }, []);

  const fetchCoaches = useCallback(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, role')
      .in('role', ['coach', 'administrator'])
      .order('first_name', { ascending: true });
    
    if (error) {
      setError(error.message);
      return;
    }
    
    setCoaches(data || []);
  }, []);

  const fetchAssignments = useCallback(async () => {
    const { data, error } = await supabase
      .from('coach_age_groups')
      .select('*');
    
    if (error) {
      setError(error.message);
      return;
    }
    
    setAssignments(data || []);
  }, []);

  useEffect(() => {
    Promise.all([fetchAgeGroups(), fetchCoaches(), fetchAssignments()]);
  }, [fetchAgeGroups, fetchCoaches, fetchAssignments]);

  const addAgeGroup = useCallback(async (newGroup: NewAgeGroup) => {
    if (!newGroup.group_name || !newGroup.birth_date_min || !newGroup.birth_date_max) {
      setError('All fields are required.');
      return;
    }

    const { error } = await supabase
      .from('age_groups')
      .insert([newGroup]);

    if (error) {
      setError(error.message);
      return;
    }

    setError(null);
    await fetchAgeGroups();
  }, [fetchAgeGroups]);

  const updateAgeGroup = useCallback(async (group: AgeGroup) => {
    if (!group.group_name || !group.birth_date_min || !group.birth_date_max) {
      setError('All fields are required.');
      return;
    }

    const { error } = await supabase
      .from('age_groups')
      .update({
        group_name: group.group_name,
        sex: group.sex,
        birth_date_min: group.birth_date_min,
        birth_date_max: group.birth_date_max,
      })
      .eq('id', group.id);

    if (error) {
      setError(error.message);
      return;
    }

    setError(null);
    setEditingGroup(null);
    await fetchAgeGroups();
  }, [fetchAgeGroups]);

  const toggleAssignment = useCallback(async (ageGroupId: number, coachId: string) => {
    const existingAssignment = assignments.find(
      (a) => a.age_group_id === ageGroupId && a.coach_id === coachId
    );

    if (existingAssignment) {
      const { error } = await supabase
        .from('coach_age_groups')
        .delete()
        .eq('id', existingAssignment.id);
      
      if (error) {
        setError(error.message);
        return;
      }
    } else {
      const { error } = await supabase
        .from('coach_age_groups')
        .insert([{ age_group_id: ageGroupId, coach_id: coachId }]);
      
      if (error) {
        setError(error.message);
        return;
      }
    }

    setError(null);
    await fetchAssignments();
  }, [assignments, fetchAssignments]);

  const startEditing = useCallback((group: AgeGroup) => {
    setEditingGroup(group);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingGroup(null);
    setError(null);
  }, []);

  return {
    ageGroups,
    coaches,
    assignments,
    error,
    loading,
    addAgeGroup,
    updateAgeGroup,
    toggleAssignment,
    startEditing,
    cancelEditing,
    editingGroup,
  };
}